import { createSelector } from 'reselect';
import { getFormValues } from 'redux-form';
import { at, compact, compose, flatten, get, groupBy, keys, map, reduce, uniq } from 'lodash/fp';
import { UnicodeEntity } from '@mews/enums';
import { FLOOR_NAMES_NAME, STATUSES_NAME, TYPES_NAME } from '@mews-commander/resources';
import { createAsyncMetaSelectors } from '@mews/framework';
import { ResourceCategoryType, Option, ResourceState } from '@mews-commander/legacy-data-structures';
import { getContainer } from '@mews-commander/legacy-app-instance';
import {
    getNonSlotSpaces,
    getUniqueResourceFloorNumbers,
    getUniqueServiceResourceFloorNumbers,
    sortFloors,
} from '@mews-commander/legacy-entities/src/selectors/spaces';
import { moduleSelector } from './module';

const getUniqueValues = (valueName: $TSFixMe) => compose(uniq, map(valueName));

export const getFloorOptionsFromFloorNumbers = (floorNumbers: $TSFixMe) =>
    compose(
        map((floorName: string) =>
            Option({
                value: floorName,
                label: floorName,
            })
        ),
        sortFloors
    )(floorNumbers);

export const getFloorOptions = createSelector(getUniqueResourceFloorNumbers, getFloorOptionsFromFloorNumbers);

export const getServiceFloorOptions = createSelector(
    getUniqueServiceResourceFloorNumbers,
    getFloorOptionsFromFloorNumbers
);

const getUniqueResourceCategoryTypes = compose(compact, getUniqueValues('resourceCategoryType'), getNonSlotSpaces);
export const getResourceCategoryTypeOptions = createSelector(
    getUniqueResourceCategoryTypes,
    getContainer,
    (uniqueTypes, container) =>
        map(
            (type) =>
                Option({
                    // @ts-expect-error $TSFixMeError: Type 'T' is not assignable to type 'string | number | null | undefined'.
                    value: type,
                    // @ts-expect-error $TSFixMeError: Argument of type 'T' is not assignable to parameter of type '"Room" | "Bed" | "Dorm" | "Apartment" | "Suite" | "Villa" |...
                    label: container().appLocalizationComponent.localizeEnumify(ResourceCategoryType.getByName(type)),
                }),
            uniqueTypes
        )
);

const getUniqueResourceStatuses = compose(getUniqueValues('state'), getNonSlotSpaces);
export const getResourceStatusOptions = createSelector(
    getUniqueResourceStatuses,
    getContainer,
    (uniqueStates, container) =>
        map(
            (status) =>
                Option({
                    // @ts-expect-error $TSFixMeError: Type 'T' is not assignable to type 'string | number | null | undefined'.
                    value: status,
                    // @ts-expect-error $TSFixMeError: Argument of type 'T' is not assignable to parameter of type '"Dirty" | "Clean" | "Inspected" | "OutOfService" | "OutOfOr...
                    label: container().appLocalizationComponent.localizeEnumify(ResourceState.getByName(status)),
                }),
            uniqueStates
        )
);

const createOption = ({ id, number, resourceCategoryName }: $TSFixMe) =>
    Option({
        value: id,
        label: number,
        secondaryText: resourceCategoryName,
    });

const getResourceAssignmentFormValues = (state: $TSFixMe, { form }: $TSFixMe) => getFormValues(form)(state) || {};

export const getFilteredResources = createSelector(
    getNonSlotSpaces,
    getResourceAssignmentFormValues,
    (resources, formValues) => {
        // @ts-expect-error $TSFixMeError: Property 'floorNames' does not exist on type '{}'.
        const { floorNames, types, statuses } = formValues;
        const filterMap = { resourceCategoryType: types, state: statuses, floorNumber: floorNames };
        return reduce(
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            (acc, curKey) => compose(flatten, compact, at(filterMap[curKey]), groupBy(curKey))(acc),
            resources
            // @ts-expect-error $TSFixMeError: No overload matches this call.
        )(keys(filterMap));
    }
);

export const getResourceOptions = createSelector(getFilteredResources, (resourcesFiltered) =>
    compose(
        map(createOption),
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        reduce((acc, cur) => {
            // @ts-expect-error $TSFixMeError: Property 'id' does not exist on type 'unknown'.
            const { id, number, resourceCategoryName } = cur;
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
            const existing = acc[id];
            if (existing) {
                // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                acc[id] = {
                    ...existing,
                    resourceCategoryName: `${existing.resourceCategoryName}${UnicodeEntity.Comma} ${resourceCategoryName}`,
                };
            } else {
                // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
                acc[id] = { id, number, resourceCategoryName };
            }
            return acc;
        }, {})
        // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
    )(resourcesFiltered)
);

export const getResourceAssignmentInitialValues = createSelector(
    getUniqueResourceFloorNumbers,
    getUniqueResourceStatuses,
    getUniqueResourceCategoryTypes,
    (floorNames, statuses, types) => ({
        [FLOOR_NAMES_NAME]: floorNames,
        [STATUSES_NAME]: statuses,
        [TYPES_NAME]: types,
    })
);

const getMeta = compose(get('meta'), moduleSelector);
export const [isFetching, hasFailed] = createAsyncMetaSelectors(getMeta);
